import React, { useCallback } from "react";

import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import type {
  CalendarProps as AntdCalendarProps,
  HeaderRender,
} from "antd/es/calendar/generateCalendar";
import cn from "classnames";
import { addMonths, subMonths } from "date-fns";

import Button from "../Button";
import { useConfigProvider } from "../ConfigProvider";

import useCalendar from "./useCalendar";
import { findSelectableDate } from "./util";

const block = bem("mmc-calendar");

export interface CalendarProps extends Omit<AntdCalendarProps<Date>, "prefixCls"> {
  className?: string;
  headerDateFormat?: string;
}

const Calendar = ({
  className,
  disabledDate,
  headerDateFormat,
  headerRender,
  value,
  ...props
}: CalendarProps) => {
  const CalendarComponent = useCalendar();
  const { formatDate, formatMessage } = useConfigProvider();

  const defaultHeaderRender: HeaderRender<Date> = useCallback(
    ({ onChange, value: panelValue }) => {
      const prevMonth = subMonths(panelValue, 1);
      const nextMonth = addMonths(panelValue, 1);

      const prevMonthTarget = findSelectableDate(prevMonth, panelValue, disabledDate);
      const nextMonthTarget = findSelectableDate(nextMonth, panelValue, disabledDate);

      const handlePrevMonthChange = () => {
        if (prevMonthTarget) {
          onChange(prevMonthTarget);
        }
      };

      const handleNextMonthChange = () => {
        if (nextMonthTarget) {
          onChange(nextMonthTarget);
        }
      };

      return (
        <div className={block("header")}>
          <Button
            className={block("nav-btn")}
            disabled={!prevMonthTarget}
            iconOnly
            leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={handlePrevMonthChange}
            size="small"
            type="text"
          />
          <span className={block("title")}>
            {formatDate(
              panelValue,
              headerDateFormat ?? formatMessage("ui.calendar.header.date.format")
            )}
          </span>
          <Button
            className={block("nav-btn")}
            disabled={!nextMonthTarget}
            iconOnly
            leftIcon={<FontAwesomeIcon icon={faChevronRight} />}
            onClick={handleNextMonthChange}
            size="small"
            type="text"
          />
        </div>
      );
    },
    [headerDateFormat, formatMessage, disabledDate, formatDate]
  );

  return (
    <CalendarComponent
      className={cn(block(), className)}
      disabledDate={disabledDate}
      headerRender={headerRender ?? defaultHeaderRender}
      value={value}
      {...props}
    />
  );
};

export default Calendar;
